import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import NoAccessPage from "./NoAccessPage";
import "react-datepicker/dist/react-datepicker.css";
import PaginationButtonToolbar from "../components/PaginatedButtonToolbar";
import CustomDateInputs from "../components/CustomDateInputs";
import { formatDateAndTimeOnline } from "../components/DateAndTimeFunctions";
import useJWTInterceptors from "../authentication/useJWTInterceptors";

const TestStationFullDataPaginated = () => {
  const isLoggedIn = useSelector((state) => state.login.accessToken);
  const { theRouteUrl } = useParams();
  const stacks = useSelector((state) => state.stacks);
  const stacksLoading = stacks.stacksLoading;
  const stacksError = stacks.stacksError;
  const stackDataOPSGA = stacks.stacksData.OPSGA;
  const information = useSelector((state) => state.information);
  const informationData = information.informationData;
  const informationError = information.informationError;
  const informationLoading = information.informationLoading;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [excelAPI, setExcelAPI] = useState("");
  const [excelError, setExcelError] = useState(null);
  const [device, setDevice] = useState();
  const [deviceID, setDeviceID] = useState();
  const [currentPageData, setCurrentPageData] = useState({});
  const [pageNumber, setPageNumber] = useState(1);
  const [totalPageNumber, setTotalPageNumber] = useState();
  const [count, setCount] = useState(0);
  const [excelloading, setExcelLoading] = useState(false);
  const [pageIsValid, setPageIsValid] = useState(true);
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  yesterday.setHours(0, 0, 0, 0);
  const [selectedStartDateAndTime, setSelectedStartDateAndTime] =
    useState(yesterday);
  const [selectedEndDateAndTime, setSelectedEndDateAndTime] = useState(
    new Date()
  );
  const baseURL = process.env.REACT_APP_BASE_URL;
  const [selectedOption, setSelectedOption] = useState("online");
  const convertToJalali = (x) => {
    const A = x.slice(0, 10);
    const B = x.slice(11, 19);
    const C = new Date(A);
    const D = new Intl.DateTimeFormat("fa-IR").format(C);
    const E = D.concat(" ", "(", B, ")");
    return E;
  };

  const fetchOnlineData = async (x) => {
    try {
      const response = await useJWTInterceptors.get(x, {
        headers: { Authorization: `Bearer ${isLoggedIn}` },
      });
      setCurrentPageData(response.data.results);
      setTotalPageNumber(Math.ceil(response.data.count / 50));
      setLoading(false);
    } catch (error) {
      setPageNumber(1);
      setError(true);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!informationLoading && !informationError && informationData) {
      const theSpecificStation = informationData.find(
        (x) => x.slug === theRouteUrl
      );

      // Set pageIsValid only if the station exists
      setPageIsValid(!!theSpecificStation);

      if (theSpecificStation) {
        const deviceName = theSpecificStation.device;
        setDevice(deviceName);
        const deviceNumber = theSpecificStation.device_id;
        setDeviceID(deviceNumber);
        const theOnlineDataApiURLString = `${baseURL}history/${deviceName}/${deviceNumber}/0?start_date=${formatDateAndTimeOnline(
          selectedStartDateAndTime
        )}&end_date=${formatDateAndTimeOnline(
          selectedEndDateAndTime
        )}&page=${pageNumber}`;
        setExcelAPI(
          `${baseURL}${deviceName.toLowerCase()}_excel/?device_id=${deviceNumber}&start_date=${formatDateAndTimeOnline(
            selectedStartDateAndTime
          )}&end_date=${formatDateAndTimeOnline(
            selectedEndDateAndTime
          )}&granularity=0`
        );
        fetchOnlineData(theOnlineDataApiURLString);
      }
    }
  }, [pageNumber, count, loading, error, isLoggedIn]);

  const downloadExcelReport = async () => {
    setCount(count + 1);
    setExcelLoading(true);

    try {
      // Make a GET request to the server to fetch the Excel file
      const response = await useJWTInterceptors.get(excelAPI, {
        headers: { Authorization: `Bearer ${isLoggedIn}` },
        responseType: "blob", // set responseType to 'blob' for binary data
      });

      // Create a blob from the binary data received in the response
      const blob = new Blob([response.data], {
        type: response.headers["content-type"],
      });

      // Create a URL for the blob and use it to create a link element
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      // Set the link's properties for download
      link.href = url;
      link.download = `${formatDateAndTimeOnline(
        selectedStartDateAndTime
      )}_${formatDateAndTimeOnline(selectedEndDateAndTime)}.xlsx`; // specify the desired file name

      // Append the link to the document body and trigger a click to start download
      document.body.appendChild(link);
      link.click();

      // Clean up the created elements after the download is initiated
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      // console.error("Error downloading Excel report:", error);
      setExcelError(error);
      // Handle the error (e.g., show a user-friendly message)
    } finally {
      setExcelLoading(false);
    }
  };

  if (!isLoggedIn) {
    return <NoAccessPage />;
  } else if (isLoggedIn && deviceID !== 105) {
    return (
      <div className="container-fluid p-2 text-medium">
        <div className="row">
          <CustomDateInputs
            type="stack"
            startDateAndTime={selectedStartDateAndTime}
            endDateAndTime={selectedEndDateAndTime}
            setSelectedStartDateAndTime={setSelectedStartDateAndTime}
            setSelectedEndDateAndTime={setSelectedEndDateAndTime}
            setSelectedOption={setSelectedOption}
            setCount={setCount}
            count={count}
            excelLoading={excelloading}
            downloadExcelReport={downloadExcelReport}
            setPageNumber={setPageNumber}
          />
          <div className="col-lg-9 col-xl-10 " style={{ overflowX: "scroll" }}>
            <div dir="ltr" className="mb-1 d flex">
              {loading || error || currentPageData?.length < 1 ? (
                ""
              ) : (
                <PaginationButtonToolbar
                  setSelectedStartDateAndTime
                  selectedOption="online"
                  pageNumber={pageNumber}
                  totalPageNumber={totalPageNumber}
                  setPageNumber={setPageNumber}
                />
              )}
            </div>

            {loading || error ? (
              ""
            ) : (
              <div className="bg-secondary p-1">
                <table
                  id="dtBasicExample"
                  className="table bg-light table-striped table-bordered table-sm mx-auto text-center"
                  style={{ overflowX: "scroll", maxWidth: "screen" }}
                  cellSpacing="0"
                  dir="rtl"
                  width="90%"
                >
                  <thead>
                    <tr>
                      {device === "OPSGA" ? (
                        <th className="IRANSansXE p-3">O2</th>
                      ) : (
                        ""
                      )}
                      {device === "OPSGA" ? (
                        <th className="IRANSansXE p-3">CO</th>
                      ) : (
                        ""
                      )}
                      {device === "OPSGA" ? (
                        <th className="IRANSansXE p-3">NO</th>
                      ) : (
                        ""
                      )}
                      {device === "OPSGA" ? (
                        <th className="IRANSansXE p-3">NO2</th>
                      ) : (
                        ""
                      )}
                      {device === "OPSGA" ? (
                        <th className="IRANSansXE p-3">SO2</th>
                      ) : (
                        ""
                      )}
                      {device === "OPSGA" && deviceID !== 104 ? (
                        <th className="IRANSansXE p-3">flow</th>
                      ) : (
                        ""
                      )}
                      <th className="IRANSansXE p-3">تاریخ و ساعت</th>
                    </tr>
                  </thead>

                  <tbody>
                    {currentPageData.map((station, index) => (
                      <tr
                        key={
                          station.ccreated_datetime ||
                          station.created_datetime ||
                          index
                        }
                      >
                        {(device === "OPSGA" &&
                          station.o2 !== null &&
                          station.o2 !== undefined) ||
                        station.o2 === 0 ? (
                          <td className="IRANSansXB">
                            <p>
                              {Number(station.o2).toFixed(2)}
                              <sub>%Vol</sub>
                            </p>
                          </td>
                        ) : (
                          <td className="IRANSansXB text text-center">-</td>
                        )}

                        {(device === "OPSGA" &&
                          station.co !== null &&
                          station.co !== undefined) ||
                        station.co === 0 ? (
                          <td className="IRANSansXB">
                            <p>
                              {Number(station.co).toFixed(0)}
                              <sub>ppm</sub>
                            </p>
                          </td>
                        ) : (
                          <td className="IRANSansXB text text-center">-</td>
                        )}

                        {(device === "OPSGA" &&
                          station.no !== null &&
                          station.no !== undefined) ||
                        station.no === 0 ? (
                          <td className="IRANSansXB">
                            <p>
                              {Number(station.no).toFixed(0)}
                              <sub>ppm</sub>
                            </p>
                          </td>
                        ) : (
                          <td className="IRANSansXB text text-center">-</td>
                        )}

                        {(device === "OPSGA" &&
                          station.no2 !== null &&
                          station.no2 !== undefined) ||
                        station.no2 === 0 ? (
                          <td className="IRANSansXB">
                            <p>
                              {Number(station.no2).toFixed(1)}
                              <sub>ppm</sub>
                            </p>
                          </td>
                        ) : (
                          <td className="IRANSansXB text text-center">-</td>
                        )}

                        {(device === "OPSGA" &&
                          station.so2 !== null &&
                          station.so2 !== undefined) ||
                        station.so2 === 0 ? (
                          <td className="IRANSansXB">
                            <p>
                              {Number(station.so2).toFixed(0)}
                              <sub>ppm</sub>
                            </p>
                          </td>
                        ) : (
                          <td className="IRANSansXB text text-center">-</td>
                        )}

                        {(device === "OPSGA" &&
                          deviceID !== 104 &&
                          station.flow !== null &&
                          station.flow !== undefined) ||
                        station.flow === 0 ? (
                          <td className="IRANSansXB">
                            <p>
                              <sub>L/min</sub>
                              {Number(station.flow).toFixed(2)}
                            </p>
                          </td>
                        ) : device === "OPSGA" && deviceID === 104 ? (
                          ""
                        ) : (
                          <td className="IRANSansXB text text-center">-</td>
                        )}

                        {station.ccreated_datetime ? (
                          <td className="ss01" dir="ltr">
                            {convertToJalali(station.ccreated_datetime)}
                          </td>
                        ) : (
                          <td className="ss01" dir="ltr">
                            {convertToJalali(station.created_datetime)}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  } else if (isLoggedIn && device === "OPSGA" && deviceID === 105) {
    return (
      <div className="container-fluid p-2 text-medium">
        <div className="row">
          <CustomDateInputs
            type="OPSGA105"
            startDateAndTime={selectedStartDateAndTime}
            endDateAndTime={selectedEndDateAndTime}
            setSelectedStartDateAndTime={setSelectedStartDateAndTime}
            setSelectedEndDateAndTime={setSelectedEndDateAndTime}
            setSelectedOption={setSelectedOption}
            setCount={setCount}
            count={count}
            excelLoading={excelloading}
            downloadExcelReport={downloadExcelReport}
            setPageNumber={setPageNumber}
          />
          <div className="col-lg-9 col-xl-10 " style={{ overflowX: "scroll" }}>
            <div dir="ltr" className="mb-1 d flex">
              {loading || error || currentPageData?.length < 1 ? (
                ""
              ) : (
                <PaginationButtonToolbar
                  setSelectedStartDateAndTime
                  selectedOption="online"
                  pageNumber={pageNumber}
                  totalPageNumber={totalPageNumber}
                  setPageNumber={setPageNumber}
                />
              )}
            </div>

            {loading || error ? (
              ""
            ) : (
              <div className="bg-secondary p-1">
                <table
                  id="dtBasicExample"
                  className="table bg-light table-striped table-bordered table-sm mx-auto text-center"
                  style={{ overflowX: "scroll", maxWidth: "screen" }}
                  cellSpacing="0"
                  dir="rtl"
                  width="90%"
                >
                  <thead>
                    <tr>
                      <th className="IRANSansXE p-3">flow</th>

                      <th className="IRANSansXE p-3">Dust</th>

                      <th className="IRANSansXE p-3">Dust_norm</th>

                      <th className="IRANSansXE p-3">Temperature</th>

                      <th className="IRANSansXE p-3">تاریخ و ساعت</th>
                    </tr>
                  </thead>

                  <tbody>
                    {currentPageData.map((station) => (
                      <tr>
                        {(device === "OPSGA" &&
                          station.flow !== null &&
                          station.flow !== undefined) ||
                        station.flow === 0 ? (
                          <td className="IRANSansXB">
                            <p>
                              {Number(station.flow).toFixed(0)}
                              <sub>
                                m<sup>3</sup>/s
                              </sub>
                            </p>
                          </td>
                        ) : (
                          <td className="IRANSansXB text text-center">-</td>
                        )}

                        {(device === "OPSGA" &&
                          station.dust !== null &&
                          station.dust !== undefined) ||
                        station.dust === 0 ? (
                          <td className="IRANSansXB">
                            <p>
                              {Number(station.dust).toFixed(0)}
                              <sub>
                                mg/m<sup>3</sup>
                              </sub>
                            </p>
                          </td>
                        ) : (
                          <td className="IRANSansXB text text-center">-</td>
                        )}

                        {(device === "OPSGA" &&
                          station.dust_norm !== null &&
                          station.dust_norm !== undefined) ||
                        station.dust_norm === 0 ? (
                          <td className="IRANSansXB">
                            <p>
                              {Number(station.dust_norm).toFixed(1)}
                              <sub>
                                mg/nm<sup>3</sup>
                              </sub>
                            </p>
                          </td>
                        ) : (
                          <td className="IRANSansXB text text-center">-</td>
                        )}

                        {(device === "OPSGA" &&
                          station.amb !== null &&
                          station.amb !== undefined) ||
                        station.amb === 0 ? (
                          <td className="IRANSansXB">
                            <p>
                              <sub>℃</sub>
                              {Number(station.amb).toFixed(1)}{" "}
                              {/* temperature */}
                            </p>
                          </td>
                        ) : (
                          <td className="IRANSansXB text text-center">-</td>
                        )}

                        {station.ccreated_datetime ? (
                          <td className="ss01" dir="ltr">
                            {convertToJalali(station.ccreated_datetime)}
                          </td>
                        ) : (
                          <td className="ss01" dir="ltr">
                            {convertToJalali(station.created_datetime)}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
};

export default TestStationFullDataPaginated;
